import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { LogOut, ShieldAlert, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Sticky banner that appears any time the current session is an admin
 * impersonating another user. Lets the admin exit back to their own session
 * with one click. The original admin token is kept in localStorage under
 * `eventism_admin_backup_token`.
 */
export default function ImpersonationBanner() {
  const { user, token, setSession } = useAuth();
  const [exiting, setExiting] = useState(false);

  const active = !!(user && user._impersonator);

  // Toggle a body class so global CSS can push the fixed Navbar (and any
  // other top-pinned chrome) down by the banner's height while we're acting
  // as another user. Keeps the nav links from getting hidden under the banner.
  useEffect(() => {
    if (active) {
      document.body.classList.add('is-impersonating');
      return () => document.body.classList.remove('is-impersonating');
    }
    return undefined;
  }, [active]);

  if (!active) return null;

  const exit = async () => {
    setExiting(true);
    try {
      // Best-effort audit-log close-out
      try { await axios.post(`${API}/admin/impersonation/end`, {}, { headers: { Authorization: `Bearer ${token}` } }); } catch (_) {}

      const backup = localStorage.getItem('eventism_admin_backup_token');
      if (!backup) {
        toast.error('Backup admin token missing — please log in again.');
        localStorage.removeItem('eventism_token');
        window.location.href = '/login';
        return;
      }
      localStorage.removeItem('eventism_admin_backup_token');
      // Re-fetch the admin user so AuthContext reflects the right identity.
      const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${backup}` } });
      setSession(backup, me.data);
      toast.success('Back to your admin session');
      // Force a clean reload to discard any user-scoped UI state
      window.location.href = '/admin/users';
    } catch (e) {
      toast.error('Could not exit impersonation cleanly — please log in again.');
      localStorage.removeItem('eventism_token');
      localStorage.removeItem('eventism_admin_backup_token');
      window.location.href = '/login';
    }
  };

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[70] bg-amber-500 text-amber-950 border-b-2 border-amber-700 shadow-md h-10"
      data-testid="impersonation-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <ShieldAlert className="w-4 h-4" />
          <span>
            Acting as <span className="font-bold">{user.name || user.email}</span>
            <span className="text-amber-900"> · admin {user._impersonator.email}</span>
          </span>
          <span className="text-xs italic text-amber-900 hidden sm:inline">All actions are logged for audit.</span>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={exit}
          disabled={exiting}
          className="bg-white/80 border-amber-700 text-amber-950 hover:bg-white"
          data-testid="exit-impersonation-btn"
        >
          {exiting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <LogOut className="w-4 h-4 mr-1.5" />}
          Exit impersonation
        </Button>
      </div>
    </div>
  );
}
